import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, ChevronDown, ChevronUp, Check, Minus, Plus, Calculator, Send, Loader2, User, Phone, Mail, MapPin, Calendar, FileText, X, Clock, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

// Mini Calendar Component
const MiniCalendar = ({ selectedDate, onSelect, blockedDates }) => {
  const [viewDate, setViewDate] = useState(new Date());
  const today = new Date(); today.setHours(0,0,0,0);

  const year = viewDate.getFullYear(), month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = viewDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const blockedSet = new Set(blockedDates || []);

  return (
    <div className="bg-white rounded-xl border border-[#E8DFD3] p-4" data-testid="catering-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-1.5 hover:bg-gray-100 rounded-lg cursor-pointer" data-testid="cal-prev"><ChevronLeft size={16} /></button>
        <span className="text-sm font-bold text-[#2C1810]">{monthName}</span>
        <button onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-1.5 hover:bg-gray-100 rounded-lg cursor-pointer" data-testid="cal-next"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {['S','M','T','W','T','F','S'].map((d,i) => <div key={i} className="text-[10px] font-semibold text-[#7a6555] py-1">{d}</div>)}
        {days.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const dateObj = new Date(year, month, day);
          const isPast = dateObj < today;
          const isBlocked = blockedSet.has(dateStr);
          const isSelected = selectedDate === dateStr;
          const isToday = dateObj.getTime() === today.getTime();
          const disabled = isPast || isBlocked;

          return (
            <button
              key={dateStr}
              onClick={() => !disabled && onSelect(dateStr)}
              disabled={disabled}
              data-testid={`cal-day-${dateStr}`}
              className={`w-8 h-8 mx-auto rounded-lg text-xs font-medium transition-all cursor-pointer
                ${isSelected ? 'bg-[#5C7A3A] text-white shadow-md' : ''}
                ${isToday && !isSelected ? 'border-2 border-[#5C7A3A] text-[#5C7A3A]' : ''}
                ${disabled ? 'text-gray-300 cursor-not-allowed' : ''}
                ${!disabled && !isSelected ? 'hover:bg-[#5C7A3A]/10 text-[#2C1810]' : ''}
                ${isBlocked && !isPast ? 'bg-red-50 text-red-300 line-through' : ''}`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Catering = () => {
  const [cateringEnabled, setCateringEnabled] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [slots, setSlots] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [guests, setGuests] = useState(50);
  const [selectedDate, setSelectedDate] = useState('');
  const [dateSlots, setDateSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});
  const [expandedCats, setExpandedCats] = useState({});
  const [showBooking, setShowBooking] = useState(false);
  const [bookingForm, setBookingForm] = useState({ customer_name: '', mobile: '', email: '', event_location: '', event_type: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/catering/status`).then(r => r.json()).then(d => setCateringEnabled(d.enabled)).catch(() => setCateringEnabled(false));
    fetch(`${API}/api/catering/categories`).then(r => r.json()).then(setCategories).catch(() => {});
    fetch(`${API}/api/catering/items`).then(r => r.json()).then(setMenuItems).catch(() => {});
    fetch(`${API}/api/catering/slots`).then(r => r.json()).then(setSlots).catch(() => {});
    fetch(`${API}/api/catering/blocked-dates`).then(r => r.json()).then(setBlockedDates).catch(() => {});
  }, []);

  // Fetch availability when date changes
  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    fetch(`${API}/api/catering/availability/${selectedDate}`)
      .then(r => r.json())
      .then(d => { setDateSlots(d.slots || []); setLoadingSlots(false); })
      .catch(() => { setDateSlots([]); setLoadingSlots(false); });
  }, [selectedDate]);

  const itemsByCategory = useMemo(() => {
    const map = {};
    menuItems.forEach(item => { if (!map[item.category]) map[item.category] = []; map[item.category].push(item); });
    return map;
  }, [menuItems]);

  const toggleItem = (item) => {
    setSelectedItems(prev => { const c = { ...prev }; if (c[item.id]) delete c[item.id]; else c[item.id] = { ...item }; return c; });
  };

  const selected = Object.values(selectedItems);
  const subtotal = selected.reduce((sum, it) => sum + it.price_per_person * guests, 0);
  const estimatedTotal = subtotal;
  const costPerGuest = guests > 0 ? Math.round(estimatedTotal / guests) : 0;

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    if (selected.length === 0) { setError('Please select at least one menu item'); return; }
    if (!selectedDate) { setError('Please select an event date'); return; }
    if (!selectedSlot) { setError('Please select a time slot'); return; }
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${API}/api/catering/booking`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...bookingForm, guests, event_date: selectedDate, time_slot_id: selectedSlot.id,
          items: selected.map(it => ({ item_id: it.id, name: it.name, price_per_person: it.price_per_person, category: it.category })),
          subtotal, estimated_total: estimatedTotal, cost_per_guest: costPerGuest
        })
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Booking failed'); }
      const result = await res.json();
      setSubmitted(result);
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  };

  // Loading check
  if (cateringEnabled === null) {
    return <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center pt-24"><div className="animate-pulse text-[#7a6555]">Loading...</div></div>;
  }

  // Catering disabled
  if (!cateringEnabled) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-4 pt-24 pb-12" data-testid="catering-disabled">
        <SEO title="Catering" />
        <Card className="max-w-md w-full border border-[#E8DFD3] shadow-lg"><CardContent className="p-8 text-center">
          <AlertCircle size={48} className="mx-auto text-[#C4844A] mb-4" />
          <h2 className="text-xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Catering Unavailable</h2>
          <p className="text-sm text-[#7a6555]">Catering bookings are currently unavailable. Please check back later.</p>
        </CardContent></Card>
      </div>
    );
  }

  // Success
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-4 pt-24 pb-12" data-testid="catering-success">
        <SEO title="Booking Confirmed" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full bg-white rounded-2xl border border-[#E8DFD3] shadow-lg p-8 text-center">
          <Check size={56} className="mx-auto text-[#5C7A3A] mb-4" />
          <h2 className="text-2xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Booking Submitted!</h2>
          <div className="bg-[#5C7A3A]/5 border border-[#5C7A3A]/20 rounded-xl p-4 mb-4">
            <p className="text-xs text-[#7a6555]">Booking Reference</p>
            <p className="text-lg font-bold text-[#5C7A3A] font-mono" data-testid="booking-ref">{submitted.booking_ref}</p>
          </div>
          <p className="text-sm text-[#7a6555] mb-2">Estimated Total: <strong>₹{submitted.estimated_total?.toLocaleString('en-IN')}</strong></p>
          <p className="text-xs text-[#7a6555] mb-6">Our catering team will contact you to confirm details and finalize the booking.</p>
          <Button onClick={() => { setSubmitted(null); setSelectedItems({}); setShowBooking(false); setSelectedDate(''); setSelectedSlot(null); setBookingForm({ customer_name: '', mobile: '', email: '', event_location: '', event_type: '', notes: '' }); }} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer">Plan Another Event</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="catering-page">
      <SEO title="Catering" description="Professional catering services by Nistala's Group. Select date, time, menu and get instant estimates." />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#2C1810]" />
        <div className="relative max-w-5xl mx-auto px-4 text-center pt-14 pb-4">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs tracking-[0.25em] uppercase text-[#D4A574] mb-3">Professional Catering Services</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl sm:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Catering by <span className="text-[#D4A574]">Nistala's</span></motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-base text-white/60 max-w-xl mx-auto">Pick your date, choose a time slot, build your menu, and get an instant estimate.</motion.p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left Column */}
          <div className="lg:col-span-2 space-y-4">

            {/* Step 1: Guests */}
            <Card className="border border-[#E8DFD3] shadow-sm bg-white" data-testid="guest-section">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div><h2 className="text-base font-bold text-[#2C1810] flex items-center gap-2"><Users size={16} className="text-[#5C7A3A]" /> Number of Guests</h2></div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setGuests(Math.max(1, guests - 10))} className="w-8 h-8 rounded-lg border border-[#E8DFD3] flex items-center justify-center hover:bg-gray-50 cursor-pointer" data-testid="guests-minus"><Minus size={14} /></button>
                    <input type="number" min="1" value={guests} onChange={e => setGuests(Math.max(1, parseInt(e.target.value) || 1))} className="w-20 h-8 text-center border border-[#E8DFD3] rounded-lg text-sm font-bold text-[#2C1810] focus:outline-none focus:ring-2 focus:ring-[#5C7A3A]/20" data-testid="guests-input" />
                    <button onClick={() => setGuests(guests + 10)} className="w-8 h-8 rounded-lg border border-[#E8DFD3] flex items-center justify-center hover:bg-gray-50 cursor-pointer" data-testid="guests-plus"><Plus size={14} /></button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Step 2: Date & Slot */}
            <Card className="border border-[#E8DFD3] shadow-sm bg-white" data-testid="date-slot-section">
              <CardContent className="p-5">
                <h2 className="text-base font-bold text-[#2C1810] mb-3 flex items-center gap-2"><Calendar size={16} className="text-[#5C7A3A]" /> Select Date & Time</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <MiniCalendar selectedDate={selectedDate} onSelect={setSelectedDate} blockedDates={blockedDates} />
                  <div>
                    {!selectedDate ? (
                      <div className="h-full flex items-center justify-center text-sm text-[#7a6555]"><Calendar size={24} className="text-[#D4A574] mr-2" /> Select a date to see available slots</div>
                    ) : loadingSlots ? (
                      <div className="h-full flex items-center justify-center"><Loader2 size={20} className="animate-spin text-[#5C7A3A]" /></div>
                    ) : dateSlots.length === 0 ? (
                      <div className="h-full flex items-center justify-center text-sm text-[#C4844A]">No slots available for this date</div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-[#7a6555] mb-2">Available Slots for {selectedDate}</p>
                        {dateSlots.map(slot => (
                          <button
                            key={slot.id}
                            onClick={() => slot.is_available && setSelectedSlot(slot)}
                            disabled={!slot.is_available}
                            data-testid={`slot-${slot.id}`}
                            className={`w-full text-left p-3 rounded-xl border transition-all cursor-pointer ${
                              selectedSlot?.id === slot.id ? 'bg-[#5C7A3A]/10 border-[#5C7A3A] ring-1 ring-[#5C7A3A]' :
                              slot.is_available ? 'border-[#E8DFD3] hover:border-[#5C7A3A]/40 hover:bg-[#FDF8F0]' :
                              'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Clock size={14} className={selectedSlot?.id === slot.id ? 'text-[#5C7A3A]' : 'text-[#7a6555]'} />
                                <span className="text-sm font-semibold text-[#2C1810]">{slot.start_time} - {slot.end_time}</span>
                              </div>
                              {selectedSlot?.id === slot.id && <Check size={14} className="text-[#5C7A3A]" />}
                            </div>
                            <p className="text-[10px] text-[#7a6555] mt-1">
                              {slot.is_available ? `${slot.remaining_bookings} bookings · ${slot.remaining_guests} guests remaining` : 'Fully booked'}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Step 3: Menu */}
            <div>
              <h2 className="text-base font-bold text-[#2C1810] mb-3 flex items-center gap-2"><FileText size={16} className="text-[#5C7A3A]" /> Select Menu Items</h2>
              {categories.length === 0 && <Card className="border border-[#E8DFD3] bg-white p-8 text-center"><p className="text-[#7a6555]">No menu items available.</p></Card>}
              {categories.map(cat => {
                const catItems = itemsByCategory[cat.name] || [];
                if (catItems.length === 0) return null;
                const isExpanded = expandedCats[cat.name];
                const selectedInCat = catItems.filter(it => selectedItems[it.id]).length;
                return (
                  <Card key={cat.id} className="border border-[#E8DFD3] shadow-sm bg-white mb-3 overflow-hidden" data-testid={`category-${cat.name.replace(/\s/g, '-')}`}>
                    <button onClick={() => setExpandedCats(prev => ({ ...prev, [cat.name]: !prev[cat.name] }))} className="w-full px-5 py-3 flex items-center justify-between hover:bg-[#FDF8F0]/50 transition-colors cursor-pointer" data-testid={`toggle-${cat.name.replace(/\s/g, '-')}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-[#2C1810]">{cat.name}</span>
                        <span className="text-xs text-[#7a6555] bg-[#FDF8F0] px-2 py-0.5 rounded-full">{catItems.length}</span>
                        {selectedInCat > 0 && <span className="text-xs text-white bg-[#5C7A3A] px-2 py-0.5 rounded-full">{selectedInCat}</span>}
                      </div>
                      {isExpanded ? <ChevronUp size={16} className="text-[#7a6555]" /> : <ChevronDown size={16} className="text-[#7a6555]" />}
                    </button>
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                          <div className="px-5 pb-4 space-y-2 border-t border-[#E8DFD3] pt-3">
                            {catItems.map(item => {
                              const isSel = !!selectedItems[item.id];
                              return (
                                <button key={item.id} onClick={() => toggleItem(item)} className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer ${isSel ? 'bg-[#5C7A3A]/8 border border-[#5C7A3A]/25' : 'bg-[#FDF8F0] border border-transparent hover:border-[#E8DFD3]'}`} data-testid={`item-${item.id}`}>
                                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${isSel ? 'bg-[#5C7A3A] border-[#5C7A3A]' : 'border-[#d4c5b0]'}`}>{isSel && <Check size={12} className="text-white" />}</div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-semibold text-[#2C1810]">{item.name}</span>
                                      <span className={`w-4 h-4 border rounded-sm flex items-center justify-center ${item.is_veg ? 'border-green-600' : 'border-red-600'}`}><span className={`w-2 h-2 rounded-full ${item.is_veg ? 'bg-green-600' : 'bg-red-600'}`}></span></span>
                                    </div>
                                    {item.description && <p className="text-xs text-[#7a6555] mt-0.5 truncate">{item.description}</p>}
                                  </div>
                                  <span className="text-sm font-bold text-[#5C7A3A] flex-shrink-0">₹{item.price_per_person}<span className="text-[10px] text-[#7a6555] font-normal">/person</span></span>
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Right: Estimate Summary (Sticky) */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 space-y-4">
              <Card className="border border-[#E8DFD3] shadow-lg bg-white" data-testid="estimate-summary">
                <div className="bg-[#2C1810] px-5 py-3 flex items-center gap-2">
                  <Calculator size={14} className="text-[#D4A574]" />
                  <h3 className="text-sm font-bold text-white">Estimated Catering Cost</h3>
                </div>
                <CardContent className="p-5">
                  {/* Date & Slot Summary */}
                  <div className="space-y-1.5 mb-3 pb-3 border-b border-[#E8DFD3]">
                    <div className="flex justify-between text-xs"><span className="text-[#7a6555]">Guests</span><span className="font-semibold text-[#2C1810]">{guests}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-[#7a6555]">Date</span><span className="font-semibold text-[#2C1810]">{selectedDate || '—'}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-[#7a6555]">Time</span><span className="font-semibold text-[#2C1810]">{selectedSlot ? `${selectedSlot.start_time} - ${selectedSlot.end_time}` : '—'}</span></div>
                  </div>

                  {selected.length === 0 ? (
                    <p className="text-sm text-[#7a6555] text-center py-4">Select menu items to see estimate</p>
                  ) : (
                    <>
                      <div className="text-xs text-[#7a6555] mb-2">{selected.length} items selected</div>
                      <div className="space-y-1.5 mb-3 max-h-[30vh] overflow-y-auto">
                        {selected.map(it => (
                          <div key={it.id} className="flex items-center justify-between text-xs" data-testid={`summary-item-${it.id}`}>
                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                              <button onClick={() => toggleItem(it)} className="text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0"><X size={10} /></button>
                              <span className="text-[#2C1810] truncate">{it.name}</span>
                            </div>
                            <span className="text-[#7a6555] flex-shrink-0 ml-2">₹{(it.price_per_person * guests).toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-[#E8DFD3] pt-3 space-y-1.5">
                        <div className="flex justify-between text-xs text-[#7a6555]"><span>Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span></div>
                        <div className="flex justify-between text-base font-bold text-[#2C1810]"><span>Estimated Total</span><span>₹{estimatedTotal.toLocaleString('en-IN')}</span></div>
                        <div className="flex justify-between text-xs text-[#5C7A3A] font-medium"><span>Cost per Guest</span><span>₹{costPerGuest.toLocaleString('en-IN')}</span></div>
                      </div>
                      <p className="text-[10px] text-[#7a6555] mt-2 italic">Estimated cost, not a confirmed quotation.</p>
                    </>
                  )}

                  <Button onClick={() => setShowBooking(true)} disabled={selected.length === 0 || !selectedDate || !selectedSlot} className="w-full mt-4 bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold py-4 cursor-pointer" data-testid="request-quote-btn">
                    Request Catering Quote <Send size={14} className="ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <AnimatePresence>
        {showBooking && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowBooking(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="relative w-full max-w-md z-10 max-h-[90vh] overflow-y-auto">
              <Card className="border border-[#E8DFD3] shadow-2xl bg-white overflow-hidden">
                <div className="bg-[#2C1810] px-6 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Request Catering Quote</h3>
                    <p className="text-xs text-white/60 mt-0.5">{selectedDate} · {selectedSlot?.start_time}-{selectedSlot?.end_time} · {guests} guests · ₹{estimatedTotal.toLocaleString('en-IN')}</p>
                  </div>
                  <button onClick={() => setShowBooking(false)} className="p-1.5 hover:bg-white/15 rounded-lg cursor-pointer" data-testid="close-booking"><X size={18} className="text-white" /></button>
                </div>
                <CardContent className="p-6">
                  {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-xl mb-4" data-testid="booking-error">{error}</div>}
                  <form onSubmit={handleSubmitBooking} className="space-y-3">
                    <div><label className="text-xs font-medium text-[#2C1810] mb-1 flex items-center gap-1"><User size={12} className="text-[#7a6555]" /> Name *</label>
                    <Input data-testid="booking-name" value={bookingForm.customer_name} onChange={e => setBookingForm(f => ({ ...f, customer_name: e.target.value }))} placeholder="Your name" required /></div>
                    <div><label className="text-xs font-medium text-[#2C1810] mb-1 flex items-center gap-1"><Phone size={12} className="text-[#7a6555]" /> Mobile *</label>
                    <Input data-testid="booking-mobile" type="tel" value={bookingForm.mobile} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setBookingForm(f => ({ ...f, mobile: v })); }} placeholder="10-digit" maxLength={10} required /></div>
                    <div><label className="text-xs font-medium text-[#2C1810] mb-1 flex items-center gap-1"><Mail size={12} className="text-[#7a6555]" /> Email</label>
                    <Input data-testid="booking-email" type="email" value={bookingForm.email} onChange={e => setBookingForm(f => ({ ...f, email: e.target.value }))} placeholder="Optional" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-xs font-medium text-[#2C1810] mb-1">Event Type *</label>
                      <Input data-testid="booking-type" list="evt-types" value={bookingForm.event_type} onChange={e => setBookingForm(f => ({ ...f, event_type: e.target.value }))} placeholder="e.g., Wedding" required />
                      <datalist id="evt-types"><option value="Wedding" /><option value="Corporate" /><option value="Birthday" /><option value="Festival" /><option value="Office Lunch" /></datalist></div>
                      <div><label className="text-xs font-medium text-[#2C1810] mb-1 flex items-center gap-1"><MapPin size={12} className="text-[#7a6555]" /> Location *</label>
                      <Input data-testid="booking-location" value={bookingForm.event_location} onChange={e => setBookingForm(f => ({ ...f, event_location: e.target.value }))} placeholder="Venue" required /></div>
                    </div>
                    <div><label className="text-xs font-medium text-[#2C1810] mb-1">Notes</label>
                    <Textarea data-testid="booking-notes" value={bookingForm.notes} onChange={e => setBookingForm(f => ({ ...f, notes: e.target.value }))} placeholder="Dietary needs, setup..." rows={2} /></div>
                    <Button type="submit" disabled={submitting} className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold py-4 cursor-pointer" data-testid="submit-booking-btn">
                      {submitting ? <><Loader2 size={14} className="animate-spin mr-2" /> Submitting...</> : <>Submit Booking <Send size={14} className="ml-2" /></>}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Catering;
